import React from 'react'
import type { TopPlayerOut } from '../../types'

interface TopPlayersSectionProps {
  scorers: TopPlayerOut[]
  assisters: TopPlayerOut[]
  rated: TopPlayerOut[]
  onPlayerClick?: (playerId: number) => void
}

interface TopPlayerCardProps {
  player: TopPlayerOut
  onPlayerClick?: (playerId: number) => void
}

function TopPlayerCard({ player, onPlayerClick }: TopPlayerCardProps) {
  const formatStatValue = () => {
    if (player.stat_type === 'rating') {
      return player.stat_value.toFixed(2)
    }
    return player.stat_value.toString()
  }

  const getStatLabel = () => {
    switch (player.stat_type) {
      case 'goals': return 'Goals'
      case 'assists': return 'Assists'
      case 'rating': return 'Rating'
      default: return ''
    }
  }

  return (
    <button
      className="top-player-card"
      onClick={() => onPlayerClick?.(player.player_id)}
      type="button"
    >
      <div className="top-player-rank">{player.rank}</div>
      <div className="top-player-photo">
        {player.photo_url ? (
          <img src={player.photo_url} alt="" />
        ) : (
          <div className="top-player-placeholder">
            {player.name.charAt(0)}
          </div>
        )}
      </div>
      <div className="top-player-info">
        <span className="top-player-name">{player.name}</span>
        <span className="top-player-team">
          {player.team_crest && (
            <img src={player.team_crest} alt="" className="top-player-team-crest" />
          )}
          {player.team_name}
        </span>
      </div>
      <div className="top-player-stat">
        <span className="top-player-stat-value">{formatStatValue()}</span>
        <span className="top-player-stat-label">{getStatLabel()}</span>
      </div>
    </button>
  )
}

interface TopPlayersGroupProps {
  title: string
  players: TopPlayerOut[]
  icon: React.ReactNode
  onPlayerClick?: (playerId: number) => void
}

function TopPlayersGroup({ title, players, icon, onPlayerClick }: TopPlayersGroupProps) {
  if (!players || players.length === 0) return null

  return (
    <div className="top-players-group">
      <div className="top-players-group-header">
        {icon}
        <span>{title}</span>
      </div>
      <div className="top-players-group-list">
        {players.map((player) => (
          <TopPlayerCard
            key={`${player.stat_type}-${player.player_id}`}
            player={player}
            onPlayerClick={onPlayerClick}
          />
        ))}
      </div>
    </div>
  )
}

export function TopPlayersSection({
  scorers,
  assisters,
  rated,
  onPlayerClick,
}: TopPlayersSectionProps) {
  const hasData = scorers.length > 0 || assisters.length > 0 || rated.length > 0

  if (!hasData) {
    return (
      <div className="top-players-empty">
        <span>Top players data not available</span>
      </div>
    )
  }

  return (
    <div className="top-players-section">
      <TopPlayersGroup
        title="Top Scorers"
        players={scorers}
        icon={<GoalIcon />}
        onPlayerClick={onPlayerClick}
      />

      <TopPlayersGroup
        title="Top Assists"
        players={assisters}
        icon={<AssistIcon />}
        onPlayerClick={onPlayerClick}
      />

      <TopPlayersGroup
        title="Top Rated"
        players={rated}
        icon={<RatingIcon />}
        onPlayerClick={onPlayerClick}
      />
    </div>
  )
}

// Simple SVG icons
function GoalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12M6 12h12" />
    </svg>
  )
}

function AssistIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z" />
    </svg>
  )
}

function RatingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
    </svg>
  )
}
